import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useStore } from '../lib/store'
import { useIsDesktop } from './index'

interface NavItem {
  to: string
  glyph: string
  label: string
  exact?: boolean
  /** Hidden entirely when false. Used by the opt-out. */
  when?: (s: NavState) => boolean
  /**
   * True once this item's question has been answered. It keeps its place in
   * the bar until then and moves to the end of the list afterwards, which puts
   * it in the overflow menu rather than removing it.
   */
  answered?: (s: NavState) => boolean
}

interface NavState {
  checkedIn: boolean
  chippedIn: boolean
  optedOut: boolean
}

/**
 * Ten destinations, four slots.
 *
 * The order here is the priority order and the first four *visible* items get a
 * column each — but Check in and Chip in are ranked, not filtered. Until you
 * answer them they hold the second and third columns, because they are the two
 * things the week actually needs from you. Once answered they move to the END
 * of the list, which drops them into the overflow menu: still reachable, no
 * longer asking. Sched and Online slide up to take their places.
 *
 * They move rather than disappear because an answer is not a commitment. "I'm
 * in" on Tuesday and "something came up" on Saturday are the same person using
 * the same screen, and a nav item that vanishes the moment you use it leaves
 * them with no way back.
 *
 * Two items are deliberately *not* here. Settings lives behind the avatar on
 * Home, where it has always been, and Standings hangs off Picks. Ten is already
 * more than a phone wants.
 */
const ITEMS: NavItem[] = [
  { to: '/', glyph: '🏠', label: 'Home', exact: true },
  { to: '/checkin', glyph: '✋', label: 'Check in', answered: (s) => s.checkedIn },
  { to: '/fund', glyph: '💵', label: 'Chip in', answered: (s) => s.chippedIn },
  { to: '/schedule', glyph: '📅', label: 'Sched' },
  { to: '/online', glyph: '👥', label: 'Online', when: (s) => !s.optedOut },
  { to: '/shouts', glyph: '📣', label: 'Shout', when: (s) => !s.optedOut },
  { to: '/challenges', glyph: '💪', label: 'Feats', when: (s) => !s.optedOut },
  { to: '/timeline', glyph: '🧾', label: 'Timeline' },
  { to: '/picks', glyph: '🎯', label: 'Picks', when: (s) => !s.optedOut },
  { to: '/food', glyph: '🍕', label: 'Food' },
]

const VISIBLE_SLOTS = 4

/**
 * Four equal columns plus an overflow button, emoji over a 10px mono label.
 *
 * The active item gets an outlined pill, not just a colour change: on a phone
 * in a dark room `bright` against `dim` is a difference you have to look for,
 * and the point of the bar is knowing where you are at a glance. A dark pill
 * with a `fill` outline rather than a `fill` ground, because these labels sit
 * under emoji and emoji ignore `color` — 💪 on Pittsburgh gold is yellow on
 * yellow.
 */
export function BottomNav() {
  const { state } = useStore()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const desktop = useIsDesktop()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => { setMenuOpen(false) }, [pathname])

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [menuOpen])

  const navState: NavState = {
    // "Answered", not "said yes". Someone who told us they can't make it has
    // finished with this question, and leaving the prompt up implies they
    // haven't.
    checkedIn: state?.attendance?.mine !== null && state?.attendance?.mine !== undefined,
    chippedIn: state?.fund?.iContributed ?? false,
    optedOut: state?.user.optedOut ?? false,
  }

  const shown = ITEMS.filter((i) => !i.when || i.when(navState))
  // Stable partition: everything unanswered in its declared order, then the
  // answered ones in theirs, appended.
  const items = [
    ...shown.filter((i) => !i.answered || !i.answered(navState)),
    ...shown.filter((i) => i.answered && i.answered(navState)),
  ]
  // Four slots is a phone constraint, not a design principle. A desktop window
  // has room for all ten across the bar, and an overflow menu there is a click
  // buying nothing. Above the breakpoint every item gets a column and the menu
  // disappears entirely.
  const slots = desktop ? items.length : VISIBLE_SLOTS
  const visible = items.slice(0, slots)
  const overflow = items.slice(slots)

  const badges: Record<string, number> = {
    '/challenges': state?.challenges.counts.needsYou ?? 0,
    '/food': state?.gameDay.active
      ? (state?.food?.orders.filter((o) => !o.deliveredAt && !o.cancelledAt).length ?? 0)
      : 0,
  }
  const picksIncomplete = !!state?.picks && state.picks.made < state.picks.total && state.picks.total > 0
  const isActive = (item: NavItem) => (item.exact ? pathname === '/' : pathname.startsWith(item.to))
  const overflowActive = overflow.some(isActive)

  const close = useCallback(() => setMenuOpen(false), [])

  return (
    <>
      {menuOpen ? (
        <>
          <button
            aria-label="Close menu"
            onClick={close}
            style={{
              position: 'fixed', inset: 0, zIndex: 28,
              background: 'rgba(0,0,0,.45)', animation: 'sfg-fade-in 140ms ease', cursor: 'default',
            }}
          />
          <div
            ref={menuRef}
            role="menu"
            aria-label="More"
            style={{
              position: 'fixed', zIndex: 29, right: 8,
              // Clear of the bar rather than touching it. At 6px the last row's
              // tap target overlapped the bar's own, and on a phone with a home
              // indicator the menu's bottom corner disappeared behind it.
              bottom: `calc(24px + var(--nav-h))`,
              minWidth: 196, maxWidth: 'calc(100vw - 16px)',
              background: 'var(--surf)', border: '1px solid var(--outline)',
              borderRadius: 16, overflow: 'hidden',
              animation: 'sfg-fade-in 140ms ease',
            }}
          >
            {overflow.map((item, i) => {
              const active = isActive(item)
              const badge = badges[item.to] ?? 0
              return (
                <button
                  key={item.to}
                  role="menuitem"
                  onClick={() => { close(); navigate(item.to) }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                    padding: '13px 16px', minHeight: 48, textAlign: 'left',
                    color: active ? 'var(--bright)' : 'var(--fg)',
                    fontWeight: active ? 700 : 500, fontSize: 15,
                    borderTop: i === 0 ? undefined : '1px solid var(--line)',
                  }}
                >
                  <span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden>{item.glyph}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
                  {badge > 0 ? (
                    <span
                      className="mono"
                      style={{
                        minWidth: 20, height: 20, padding: '0 6px', borderRadius: 999,
                        background: 'var(--fill)', color: 'var(--ink)',
                        fontSize: 11, fontWeight: 700, lineHeight: '20px', textAlign: 'center',
                      }}
                    >
                      {badge}
                    </span>
                  ) : item.to === '/picks' && picksIncomplete ? (
                    <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--bright)' }} />
                  ) : null}
                </button>
              )
            })}
          </div>
        </>
      ) : null}

      <nav
        aria-label="Main"
        style={{
          position: 'sticky', bottom: 0, zIndex: 30, flex: '0 0 auto',
          background: 'var(--surf)', borderTop: '1px solid var(--line)',
          padding: `8px 8px calc(18px + env(safe-area-inset-bottom, 0px))`,
        }}
      >
      {/*
        The bar's ground and top rule run the full width of the window; the row
        of items inside it stops at the same edge as the header and the page,
        so the three do not disagree on where the app is.
      */}
      <div
        className="sfg-shell"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr)) ${overflow.length ? '56px' : ''}`.trim(),
          columnGap: 4,
          alignItems: 'center',
        }}
      >
        {visible.map((item) => {
          const active = isActive(item)
          const badge = badges[item.to] ?? 0
          const dot = item.to === '/picks' && picksIncomplete
          return (
            <NavLink
              key={item.to}
              to={item.to}
              aria-current={active ? 'page' : undefined}
              style={{
                position: 'relative', minHeight: 52, textDecoration: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <span
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  gap: 4, width: '100%', padding: '6px 2px 5px', borderRadius: 14,
                  textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 10,
                  background: active ? 'var(--bg)' : 'transparent',
                  border: `1px solid ${active ? 'var(--fill)' : 'transparent'}`,
                  color: active ? 'var(--bright)' : 'var(--dim)',
                  fontWeight: active ? 700 : 400,
                  transition: 'background 140ms ease, color 140ms ease, border-color 140ms ease',
                }}
              >
                <span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden>{item.glyph}</span>
                <span style={{ lineHeight: 1, whiteSpace: 'nowrap' }}>{item.label}</span>
              </span>
              {badge > 0 ? (
                <span
                  aria-label={`${badge} needing attention`}
                  style={{
                    position: 'absolute', top: 0, right: '50%', marginRight: -22,
                    minWidth: 17, height: 17, padding: '0 4px', borderRadius: 999,
                    background: 'var(--fill)', color: 'var(--ink)',
                    fontSize: 10, fontWeight: 700, lineHeight: '17px',
                  }}
                >
                  {badge}
                </span>
              ) : dot ? (
                <span
                  style={{
                    position: 'absolute', top: 3, right: '50%', marginRight: -16,
                    width: 6, height: 6, borderRadius: 999, background: 'var(--bright)',
                  }}
                />
              ) : null}
            </NavLink>
          )
        })}

        {overflow.length ? (
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="More"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            style={{
              position: 'relative', minHeight: 52,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <span
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 44, height: 40, borderRadius: 14,
                background: menuOpen || overflowActive ? 'var(--bg)' : 'transparent',
                border: `1px solid ${menuOpen || overflowActive ? 'var(--fill)' : 'transparent'}`,
                color: menuOpen || overflowActive ? 'var(--bright)' : 'var(--dim)',
                fontSize: 20, letterSpacing: '0.06em', lineHeight: 1,
              }}
            >
              ⋯
            </span>
            {/* One dot for everything hiding behind the menu, so a badge in
                there is not invisible until you open it. */}
            {overflow.some((i) => (badges[i.to] ?? 0) > 0)
              || (picksIncomplete && overflow.some((i) => i.to === '/picks')) ? (
                <span
                  style={{
                    position: 'absolute', top: 6, right: 6,
                    width: 7, height: 7, borderRadius: 999, background: 'var(--bright)',
                  }}
                />
              ) : null}
          </button>
        ) : null}
      </div>
      </nav>
    </>
  )
}

/** Persistent bar while offline, with the pending-write count (D103). */
export function OfflineBar() {
  const { online, pending } = useStore()
  if (online && pending === 0) return null
  return (
    <div
      role="status"
      className="mono"
      style={{
        flex: '0 0 auto', background: 'var(--fill)', color: 'var(--ink)',
        padding: '10px 16px', fontSize: 11,
        paddingTop: `calc(10px + env(safe-area-inset-top, 0px))`,
      }}
    >
      {online
        ? `Saving · ${pending} change${pending === 1 ? '' : 's'} pending`
        : `Offline${pending ? ` · ${pending} change${pending === 1 ? '' : 's'} pending` : ''}`}
    </div>
  )
}
